import { Injectable, inject } from '@angular/core';
import { Storage, getDownloadURL, ref, uploadBytes } from '@angular/fire/storage';
import { resizeImageToBlob } from '../utils/image.utils';

/** Download URLs stored on the owning document (location, user). */
export interface Avatar {
  avatarUrl: string;
  avatarThumbnailUrl: string;
}

const CACHE_CONTROL = 'public, max-age=31536000, immutable';
const THUMBNAIL_MAX_DIMENSION = 256;

@Injectable({ providedIn: 'root' })
export class AvatarService {
  readonly #storage = inject(Storage);

  /**
   * Uploads (or replaces) an avatar as full-size + client-generated thumbnail at
   * `${folder}/avatar/full` and `${folder}/avatar/thumb`. Fixed paths overwrite in place,
   * so no old files are left behind; overwriting rotates the download token, so the
   * returned URLs are fresh (cache-busting) despite the immutable cacheControl.
   */
  async upload(folder: string, file: File): Promise<Avatar> {
    const fullRef = ref(this.#storage, `${folder}/avatar/full`);
    const thumbRef = ref(this.#storage, `${folder}/avatar/thumb`);
    const thumbnail = await resizeImageToBlob(file, THUMBNAIL_MAX_DIMENSION);

    await Promise.all([
      uploadBytes(fullRef, file, { contentType: file.type, cacheControl: CACHE_CONTROL }),
      uploadBytes(thumbRef, thumbnail, { contentType: 'image/jpeg', cacheControl: CACHE_CONTROL }),
    ]);

    const [avatarUrl, avatarThumbnailUrl] = await Promise.all([
      getDownloadURL(fullRef),
      getDownloadURL(thumbRef),
    ]);
    return { avatarUrl, avatarThumbnailUrl };
  }
}
